import { injectable } from "inversify";
import { Either, right } from "fp-ts/lib/Either";
import ErrorEntity from "../../../domain/entities/error.entity";
import QAResponseQuestionEntity from "../../../domain/entities/qa_response_question.entity";
import QAResultEntity from "../../../domain/entities/qa_result.entity";
import SearchResultEntity from "../../../domain/entities/search_result.entity";
import QAResumeEntity from "../../../domain/entities/qa_resume.entity";
import HumanAgentEntity from "../../../domain/entities/human_agent.entity";
import Testing from "../../../presentation/utils/Testing";
import QAResumeRepository from "../../../domain/repositories/qa_resume.repository";
import { fakeAgent } from "../qa/qa.repository.fake";
import QACategoryEntity from "../../../domain/entities/qa_category.entity";
import { fakeHumanAgent } from "../human_agent/human_agent.repository.fake";

@injectable()
class QAResumeRepositoryFake implements QAResumeRepository {
    downloadAll = async (_: { query?: string; humanAgentId?: string; from?: Date; to?: Date; }): Promise<Either<ErrorEntity, void>> => {
        await Testing.sleeper(1000);
        return right(undefined);
    }
    updateResponseQuestion = async (response: QAResponseQuestionEntity): Promise<Either<ErrorEntity, QAResponseQuestionEntity>> => {
        await Testing.sleeper(1000);
        return right(response);
    };

    uploadAudioForTranscription = async (_: { audio: File, humanAgentId: string, iaAgentId: string, onCallBack?: (progress: number, message: string) => void }): Promise<Either<ErrorEntity, string>> => {
        await Testing.sleeper(1000);
        return right("https://samplelib.com/lib/preview/mp3/sample-3s.mp3");
    };

    getAudioResults = async (_: {
        jobId: string, onCallBack?: (progress: number, message: string) => void,
        humanAgent?: HumanAgentEntity,
    }): Promise<Either<ErrorEntity, QAResultEntity>> => {
        await Testing.sleeper(1000);
        return right(new QAResultEntity({
            humanAgent: _.humanAgent ?? fakeHumanAgent,
            transcription: {
                conversation: [
                    {
                        senderIsClient: false,
                        time: Math.random() * 4800,
                        transcription: "Hello, Customer service",
                    },
                    {
                        senderIsClient: true,
                        time: Math.random() * 4800,
                        transcription: "Hello I need bla bla",
                    },
                    {
                        senderIsClient: false,
                        time: Math.random() * 4800,
                        transcription: "Hello, Customer service",
                    },
                    {
                        senderIsClient: true,
                        time: Math.random() * 4800,
                        transcription: "Hello I need bla bla",
                    },
                    {
                        senderIsClient: false,
                        time: Math.random() * 4800,
                        transcription: "Hello, Customer service",
                    },
                    {
                        senderIsClient: true,
                        time: Math.random() * 4800,
                        transcription: "Hello I need bla bla",
                    },
                    {
                        senderIsClient: false,
                        time: Math.random() * 4800,
                        transcription: "Hello, Customer service",
                    },
                    {
                        senderIsClient: true,
                        time: Math.random() * 4800,
                        transcription: "Hello I need bla bla",
                    },
                ]
            },
            id: "123",
            audioUrl: "https://samplelib.com/lib/preview/mp3/sample-3s.mp33",
            categories: fakeAgent.categories.map((category: QACategoryEntity, index: number) => ({
                ...category,
                id: `${category.id}-${index}`,
            })),
        }));
    }

    search = async (_: { query?: string, humanAgentId?: string, page: number, itemsPerPage: number, from?: Date, to?: Date, audioName?: string }): Promise<Either<ErrorEntity, SearchResultEntity<QAResumeEntity>>> => {
        await Testing.sleeper(1000);
        const allData = Array.from({ length: 100 }, (_, i) => ({
            id: `${i}`,
            job_id: `job-${i}`,
            audio_name: `audio-${i}.mp3`,
            job_status: "completed",
            qa_agent_name: "Agent Name",
            operation_name: "Operation Name",
            client_name: "Client Name",
            human_agent_name: "Team Member",
            start_at: new Date(),
            end_at: new Date(),
            created_at: new Date(),
            agent_score: Math.random() * 100,
            customer_score: Math.random() * 100,
            average_score: Math.random() * 100,
            agent_sentiment: "positive",
            customer_sentiment: "neutral",
            call_type: "inbound",
            target: 80,
            achieved: Math.random() * 100,
        }));

        let filtered = allData;
        if (_.audioName) {
            filtered = filtered.filter((item) =>
                item.audio_name.toLowerCase().includes(_.audioName!.toLowerCase())
            );
        }

        const start = (_.page - 1) * _.itemsPerPage;
        const paginated = filtered.slice(start, start + _.itemsPerPage);

        return right({
            total: filtered.length,
            data: paginated,
            itemsPerPage: _.itemsPerPage,
            page: _.page,
            pages: Math.ceil(filtered.length / _.itemsPerPage),
        });

    };
}

export default QAResumeRepositoryFake;